import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Tooltip, Button, FormControl, FormGroup,
	HelpBlock, OverlayTrigger, Popover } from 'react-bootstrap';
import { MoreLess, JsonSchema, Typeahead, Toggle } from 'axway-react-ui';
import cn from 'classnames';
import FieldEditorDialog from './FieldEditorDialog';

const DEFAULT_VALUES = {
	object: '{}',
	array: '[]',
	string: '""',
	number: '0',
	boolean: 'false',
	null: 'null',
	jsonpath: '$'
};

// schema formats that render as a button that opens a custom editor
const buttonFieldFormats = FieldEditorDialog.customEditorFormats;
// schema formats for strings which have a dialog editor
const dialogEditFormats = [ 'multiline', ...buttonFieldFormats ];

/**
 * Configuration fields widget.
 */
class NodeConfigField extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.onChangeValueLocal = this.onChangeValueLocal.bind(this);
		this.onChangeValue = this.onChangeValue.bind(this);
		this.onChangeType = this.onChangeType.bind(this);
		this.onEnable = this.onEnable.bind(this);
		this.onScrollValue = this.onScrollValue.bind(this);
		this.onDelete = this.onDelete.bind(this);

		this.inputTypeLabels = {
			jsonpath: this.intl.formatMsg('Selector'),
			string: this.intl.formatMsg('String'),
			number: this.intl.formatMsg('Number'),
			boolean: this.intl.formatMsg('Boolean'),
			object: this.intl.formatMsg('Object'),
			array: this.intl.formatMsg('Array'),
			null: this.intl.formatMsg('Null'),
			credential: this.intl.formatMsg('Credential')
		};

		this.valueLabels = {
			true: this.intl.formatMsg('True'),
			false: this.intl.formatMsg('False')
		};

		// These map to "buttonFieldFormats"
		this.buttonFormatLabels = {
			mustache: this.intl.formatMsg('Edit Mustache'),
			javascript: this.intl.formatMsg('Edit JavaScript'),
			xml: this.intl.formatMsg('Edit')
		};

		this.state = {
			value: props.value,
			editorDialog: {
				show: false,
				json: props.valueType !== 'string'
			}
		};
	}

	componentWillReceiveProps(props) {
		this.setState({
			value: props.value,
			editorDialog: {
				...this.state.editorDialog,
				json: props.valueType !== 'string'
			}
		});
	}

	componentDidMount() {
		this.autoResizeValue();
	}

	componentDidUpdate() {
		this.autoResizeValue();
	}

	autoResizeValue() {
		if (this.typeEl) {
			// Clear positioning in case of type change
			this.typeEl.style.top = 0;
		}

		if (this.valueEl) {
			this.valueEl.style.height = 'auto';
			this.valueEl.style.height = `${this.valueEl.scrollHeight}px`;
		}
	}

	onScrollValue() {
		if (this.typeEl && this.valueEl) {
			// Reposition the type select at the top of the value.
			this.typeEl.style.top = `${this.valueEl.parentElement.scrollTop}px`;
		}
	}
	/**
	 * Toggles the enabled state of the parameter.
	 *
	 * @param {boolean} toggle - The toggled state of the parameter.
	 */
	onEnable(toggle) {
		const { id, onEnable, valueType, schema } = this.props;
		const { group } = schema;

		const defaultValue = this.transformValue(valueType);
		onEnable && onEnable(id, toggle, valueType, defaultValue, group);
	}

	/**
	 * When the type of a field is changed, attempts to transform the user inputed value into
	 * the new type.
	 *
	 * @param {string} type The new type of the field.
	 * @returns {string} Our best attempt at transforming the value.
	 */
	transformValue(type) {
		const { schema, credentials } = this.props;

		let newValue = this.state.value;
		const isStringEnum = schema.enum && schema.type === 'string';
		const isNumberEnum = schema.enum
			&& NodeConfigField.supportedEnumTypes.includes(schema.type);

		if (isStringEnum && type === 'string') {
			// coming from selector type
			if (schema.enum.includes(newValue)) {
				newValue = JSON.stringify(newValue);
			} else {
				newValue = JSON.stringify(schema.enum[0]);
			}
		} else if (isNumberEnum && type === 'number') {
			// coming from selector type
			if (!schema.enum.includes(parseFloat(newValue))) {
				newValue = JSON.stringify(schema.enum[0]);
			}
		} else {
			// can't convert value (doesn't make sense) between these types and any other type
			const nonConvertableTypes = [ 'null', 'boolean', 'credential' ];
			const toNonConvertableType = nonConvertableTypes.includes(type);
			const fromNonConvertableType = nonConvertableTypes.includes(this.props.valueType);

			// 1. to or from non-convertable type - default value
			// 2. no current value or default value when changing types - default value
			// 3. convert from string - parse value
			// 4. convert to string - stringify value
			// 5. convert to number - clean value or default

			if (toNonConvertableType || fromNonConvertableType
				|| !this.state.value || this.state.value === DEFAULT_VALUES[this.props.valueType]) {
				if (type === 'credential') {
					newValue = credentials && Object.keys(credentials)[0] || '';
				} else {
					newValue = DEFAULT_VALUES[type] || '';
				}
			} else {
				if (this.props.valueType === 'string') {
					// strings are quoted so remove the quotes if it has them
					try {
						newValue = JSON.parse(newValue);
					} catch (e) {
						// ignore
					}
				}

				if (type === 'string') {
					// should be a stringified string
					newValue = JSON.stringify(newValue);
				} else if (type === 'number') {
					// should be a proper number
					newValue = `${parseFloat(newValue) || DEFAULT_VALUES[type]}`;
				}
			}
		}

		return newValue;
	}

	onChangeType(type) {
		const { id, onChange, valueType, schema } = this.props;
		// Don't emit if there are no changes
		if (valueType === type) {
			return;
		}
		const { group } = schema;

		onChange && onChange(id, 'valueType', type, group);

		const newValue = this.transformValue(type);

		if (newValue !== this.state.value) {
			onChange && onChange(id, 'value', newValue, group);
		}
	}

	onChangeValue(value) {
		const { valueType, schema } = this.props;
		const { group } = schema;

		if (valueType === 'number') {
			// Clean up number before comparing.
			const cleanVal = `${parseFloat(value) || 0}`;
			if (value === cleanVal && this.props.value === value) {
				// No change
				return;
			}
			value = cleanVal;
		} else if (this.props.value === value) {
			// No change
			return;
		}
		const { id, onChange } = this.props;
		onChange && onChange(id, 'value', value, group);

		// TODO: Look into optimizing this if we refactor, this will
		// rerender twice and is executed every time the value changes.
		// We need this to happen only when we enforce the initialType
		onChange && onChange(id, 'valueType', valueType, group);
	}

	onDelete() {
		const { id, onDelete, schema } = this.props;
		const { group } = schema;

		onDelete && onDelete(id, group);
	}

	onChangeValueLocal(value) {
		// Don't want to issue change on each keystroke, so holding
		// changes in local state until blur.
		this.setState({
			value
		});
	}

	renderSchema() {
		const { id, name, schema } = this.props;
		let content = null;

		// Type label to display.
		let label;
		if (schema.type) {
			label = schema.type;
		} else if (!schema.$ref) {
			label = this.intl.formatMsg('any');
		} else {
			label = this.intl.formatMsg('schema');
		}

		const simplifiedSchema = Object.assign({}, schema);
		// we don't need to display schema properties which we already have UI for.
		delete simplifiedSchema.group;
		delete simplifiedSchema.description;
		delete simplifiedSchema.type;
		delete simplifiedSchema.title;
		delete simplifiedSchema.default;
		delete simplifiedSchema.options;
		// currently all the handled formats are "dialogEditFormats"
		// although "dialogEditFormats" may be a subset of all the formats
		// we have special UI for in the future. In that case this needs to
		// include all of the handled formats.
		if (dialogEditFormats.includes(schema.format)) {
			delete simplifiedSchema.format;
		}

		let schemaOverlay;
		// if simplified schema still has extra properties then render the schema
		if (Object.keys(simplifiedSchema).length) {
			// Schema overlay
			schemaOverlay = (
				<Popover id={`schema${id}`} className="schema-view" >
					<div>
						<JsonSchema
							schema={simplifiedSchema}
							title={name}
						/>
					</div>
				</Popover>
			);
		}

		content = (
			<div className="schema">
				<div className="text">
					{ label }
				</div>
				{ schemaOverlay && (
					<div className="view">
						<OverlayTrigger
							placement="left"
							trigger="click"
							rootClose
							overlay={schemaOverlay}
						>
							<Button className=" gg icon-GG-info text" />
						</OverlayTrigger>
					</div>
				)}
			</div>
		);

		return content;
	}

	renderEnableSwitch() {
		const { required, enabled, readOnly } = this.props;
		// Only render the switch if disabling is an option
		if (readOnly || enabled === undefined || required) {
			return null;
		}

		const tooltipMsg = enabled
			? this.intl.formatMsg('This parameter is enabled. Click the slider to disable it.')
			: this.intl.formatMsg('This parameter is disabled. Click the slider to enable it.');
		const tooltip = (
			<Tooltip id="tooltip">{this.intl.formatMsg(`${tooltipMsg}`)}</Tooltip>
		);

		return (
			<FormGroup className="enabled" controlId="enabledCtrl">
				<OverlayTrigger
					delay={1000}
					delayHide={0}
					placement="left"
					overlay={tooltip}
					trigger={[ 'hover' ]}
				>
					<span>
						<Toggle
							checked={enabled}
							onChange={(e) => this.onEnable(e.target.checked)}
						/>
					</span>
				</OverlayTrigger>
			</FormGroup>
		);
	}

	//
	// Render the banner containing the name, type and enabled toggle
	//
	renderBanner() {
		const { name, enabled, required, showSchema, deletable } = this.props;
		// outputs will always render schema while parameters will
		// have the schema only when enabled or required
		const schema = !deletable && (enabled || required || showSchema)
			? this.renderSchema() : null;

		return (
			<div className="banner">
				<div className="title">
					<div className="name">
						{name}
					</div>
					{schema}
				</div>

				{deletable ? (
					<span
						className="delete-action gg icon-GG-trash2"
						onClick={this.onDelete}
					/>
				) : this.renderEnableSwitch()}
			</div>
		);
	}

	//
	// Render the description an expandable moreless.
	//
	renderDescription() {
		const { description } = this.props;

		if (!description) {
			return;
		}

		return (
			<div className="description">
				<MoreLess>
					{ description }
				</MoreLess>
			</div>
		);
	}

	//
	// Render the default value if available.
	//
	renderDefaultValue() {
		const { schema } = this.props;
		const defaultValue = schema ? schema.default : undefined;

		if (defaultValue === undefined) {
			return null;
		}

		return (
			<div className="default-value">
				<MoreLess>
					{ this.intl.formatMsg('default: ') }
					{ JSON.stringify(defaultValue) }
				</MoreLess>
			</div>
		);
	}

	//
	// Render the input field.
	//
	renderInput() {
		const {
			id, label, enabled, required, inputTypes, schema, value, valueType,
			valueFormat, invalid, selectors, credentials, readOnly, showSchema
		} = this.props;

		// Don't render the input for non-required and disabled fields.
		if (!required && enabled !== undefined && !enabled) {
			return null;
		}

		let availableTypes;
		const credentialNames = (credentials && Object.keys(credentials)) || [];
		const credentialLength = credentialNames.length;
		const type = schema.type === 'integer' ? 'number' : schema.type;

		if (!type || inputTypes.indexOf(type) === -1) {
			// No schema type or unknown - get all input types.
			// credential type will be filtered out for non authz parameters
			if (credentials) {
				availableTypes = inputTypes;
			} else {
				availableTypes = inputTypes.filter(t => t !== 'credential');
			}
		} else {
			// Known schema type - get subset of [jsonpath & type]
			// include credential if credentials were supplied to the component
			availableTypes = inputTypes.filter((t) => {
				return (t === 'credential' && credentials)
					|| t === 'jsonpath' || t === type || t === valueType;
			});
		}

		// Did the validation say the current type is invalid?
		const invalidType = invalid === 'INVALID_TYPE';

		let checked;
		let inputfield;
		this.valueEl = null;
		const inpId = `${id}Value`;
		const isEnum = schema.enum && schema.enum.length
			&& (type === 'string' || type === 'number');

		switch (valueType) {
			case 'string':
				{
					let val = this.state.value || '';
					try {
						const parsed = JSON.parse(val);
						val = typeof parsed === 'string' ? parsed : val;
					} catch (e) {
					// Not a valid json string, display as is.
					}

					if (isEnum) {
						inputfield = (
							<FormControl
								id={inpId}
								componentClass="select"
								className="value selectDropdown"
								onChange={(e) => this.onChangeValue(JSON.stringify(e.target.value))}
								value={val}
							>
								{invalid && val && (
									<option
										key={`${inpId}${val}`}
										disabled
										value={val}
									>
										{val}
									</option>
								)}
								{schema.enum.map(value => (
									<option
										key={`${inpId}${value}`}
										value={value}
									>
										{value}
									</option>
								))}
							</FormControl>
						);

					} else if (valueFormat === 'multiline') {
						inputfield = (
							<FormControl
								id={inpId}
								// Needed for textarea controls
								inputRef={(e) => { this.valueEl = e; }}
								className="value string multiline"
								componentClass="textarea"
								autoComplete="off"
								autoCorrect="off"
								spellCheck="false"
								placeholder={this.intl.formatMsg('Enter a string')}
								onChange={
									(e) => this.onChangeValueLocal(JSON.stringify(e.target.value))
								}
								onBlur={
									(e) => this.onChangeValue(JSON.stringify(e.target.value))
								}
								value={val}
								readOnly={readOnly}
							/>
						);
					} else if (buttonFieldFormats.includes(valueFormat)) {
						inputfield = (
							<FormControl
								id={inpId}
								className="value string format btn btn-default"
								componentClass="button"
								readOnly={readOnly}
								onClick={() => {
									this.setState({
										editorDialog: {
											show: true,
											json: false
										}
									});
								}}
							>
								{this.buttonFormatLabels[valueFormat]}
								<span className="gg icon-GG-pencil" />
							</FormControl>
						);
					} else {
						inputfield = (
							<FormControl
								id={inpId}
								type="text"
								className="value string"
								autoComplete="off"
								autoCorrect="off"
								spellCheck="false"
								placeholder={`${this.intl.formatMsg('Enter a string')}`}
								onChange={
									(e) => this.onChangeValueLocal(JSON.stringify(e.target.value))
								}
								onBlur={
									(e) => this.onChangeValue(JSON.stringify(e.target.value))
								}
								value={val}
								readOnly={readOnly}
							/>
						);
					}
				}
				break;
			case 'number':
				const val = this.state.value || '';
				if (isEnum) {
					inputfield = (
						<FormControl
							id={inpId}
							componentClass="select"
							className="value selectDropdown"
							onChange={(e) => this.onChangeValue(e.target.value)}
							value={val}
							readOnly={readOnly}
						>
							{invalid && (
								<option
									key={`${inpId}${val}`}
									disabled
									value={val}
								>
									{val}
								</option>
							)}
							{schema.enum.map(value => (
								<option
									key={`${inpId}${value}`}
									value={value}
								>
									{value}
								</option>
							))}
						</FormControl>
					);
					break;
				}
				inputfield = (
					<FormControl
						id={inpId}
						type="number"
						className="value number"
						autoComplete="off"
						autoCorrect="off"
						spellCheck="false"
						placeholder={`${this.intl.formatMsg('Enter a number')}`}
						onChange={(e) => this.onChangeValueLocal(e.target.value)}
						onBlur={(e) => this.onChangeValue(e.target.value)}
						value={val}
						readOnly={readOnly}
					/>
				);
				break;
			case 'boolean':
				checked = !value ? false : !!value.match(/true/i);
				inputfield = (
					<div className="value boolean">
						<Toggle
							id={inpId}
							checked={checked}
							disabled={readOnly}
							onChange={(e) => this.onChangeValue(JSON.stringify(e.target.checked))}
						/>

						<FormControl.Static>{`${this.valueLabels[checked]}`}</FormControl.Static>
					</div>
				);
				break;
			case 'object':
				inputfield = (
					<FormControl
						id={inpId}
						// Needed for textarea controls
						inputRef={(e) => { this.valueEl = e; }}
						value={this.state.value || ''}
						className="value object"
						componentClass="textarea"
						placeholder={this.intl.formatMsg('Enter an object')}
						onChange={(e) => this.onChangeValueLocal(e.target.value)}
						onBlur={(e) => this.onChangeValue(e.target.value)}
						readOnly={readOnly}
					/>
				);
				break;
			case 'array':
				inputfield = (
					<FormControl
						id={inpId}
						// Needed for textarea controls
						inputRef={(e) => { this.valueEl = e; }}
						value={this.state.value || ''}
						className="value array"
						componentClass="textarea"
						placeholder={this.intl.formatMsg('Enter an array')}
						onChange={(e) => this.onChangeValueLocal(e.target.value)}
						onBlur={(e) => this.onChangeValue(e.target.value)}
						readOnly={readOnly}
					/>
				);
				break;
			case 'null':
				inputfield = (
					<FormControl.Static className="value null">
						{ this.intl.formatMsg('null') }
					</FormControl.Static>
				);
				break;
			case 'credential':
				const initValue = this.state.value || '';
				const invalidNames = [];

				// What we display if no credential has been set on the flow node
				const isInvalid = !(credentials && credentials[this.state.value]);
				const placeholderText = this.intl.formatMsg('Choose a credential');

				// Include the name if it wasn't a registered credential. To give context
				if (credentialNames.indexOf(initValue) < 0) {
					credentialNames.push(initValue);
					invalidNames.push(initValue);
				}

				if (readOnly) {
					inputfield = (
						<FormControl
							id={inpId}
							type="text"
							className="value string"
							autoComplete="off"
							autoCorrect="off"
							spellCheck="false"
							value={this.state.value}
							readOnly={readOnly}
						/>);
					break;
				}

				inputfield = (
					<FormControl
						id={inpId}
						componentClass="select"

						className={`value selectDropdown${isInvalid ? ' empty' : ''}`}
						onChange={(e) => this.onChangeValue(e.target.value)}
						value={initValue}
						disabled={credentialLength === 0}
					>
						{credentialNames.map(credentialName => {
							// A node key could be an empty string so make sure if its '' that its
							// registered
							const isNewNode = !(credentials && credentials[credentialName])
								&& credentialName === '';
							return (
								<option
									key={`${inpId}${credentialName}`}
									value={credentialName}
									disabled={invalidNames.indexOf(credentialName) >= 0}
								>
									{isNewNode ? placeholderText : credentialName}
								</option>);
						})}
					</FormControl>
				);
				break;
			case 'jsonpath':
			default:
				if (readOnly) {
					// don't render a typeahead if we want to keep this readOnly
					// since we can't control the readOnly state of the typeahead
					inputfield = (
						<FormControl
							id={inpId}
							type="text"
							className="value string"
							autoComplete="off"
							autoCorrect="off"
							spellCheck="false"
							value={this.state.value}
							readOnly={readOnly}
						/>);
					break;
				}
				inputfield = (
					<Typeahead
						ref={(input) => { this.input = input ? input : this.input; }}
						id={inpId}
						emptyLabel={null}
						options={selectors}
						placeholder="$"
						paginationText={this.intl.formatMsg('Show more...')}
						value={this.state.value || ''}
						onInputChange={this.onChangeValueLocal}
						onBlur={(e) => this.onChangeValue(e.target.value)}
						hideLastMatch
						onMenuShow={() => {
							const element = ReactDOM.findDOMNode(this.input);
							element && element.scrollIntoView();
						}}
					/>
				);
		}

		const actions = (valueType === 'object' || valueType === 'array'
			|| (valueType === 'string' && valueFormat === 'multiline')) && (
			<div className="actions">
				<Button
					className=" expand-button gg icon-GG-expand text"
					onClick={() => {
						this.setState({
							editorDialog: {
								show: true,
								json: valueType !== 'string'
							}
						});
					}}
				/>
			</div>
		);

		// Only render the type selection if there is a selection
		let typeSelect;

		if (readOnly && !showSchema) {
			typeSelect = (
				<span className="readonly">{this.inputTypeLabels[valueType]}</span>
			);
		} else if (availableTypes.length > 1) {
			typeSelect = (
				<div
					className="type"
					ref={(e) => { this.typeEl = e; }}
				>
					{actions}
					<FormControl
						componentClass="select"
						className={cn({ invalid: invalidType })}
						onChange={(e) => this.onChangeType(e.target.value)}
						value={valueType || 'jsonpath'}
					>
						{
							availableTypes.map(
								(t) => (
									<option
										disabled={(t === 'credential' && !credentialLength)
											|| (invalidType && t === valueType)}
										key={t}
										value={t}
									>
										{this.inputTypeLabels[t]}
									</option>
								)
							)
						}
					</FormControl>
				</div>
			);
		} else if (availableTypes.includes('object')) {
			typeSelect = (
				<div
					className="type"
					ref={(e) => { this.typeEl = e; }}
				>
					{actions}
				</div>
			);
		}
		const dropdown = valueType === 'jsonpath';
		return (
			<div className={cn('input', valueType, valueFormat, { dropdown })}>
				{!!label && (
					<label htmlFor={inpId} className="label">{label}</label>
				)}
				<div
					className={cn('field', 'form-control', { dropdown })}
					onScroll={this.onScrollValue}
					readOnly={readOnly}
				>
					{inputfield}
					{typeSelect}
				</div>
			</div>
		);
	}

	renderFieldEditModal() {
		const { name, schema, valueType, valueFormat } = this.props;
		const { editorDialog } = this.state;

		const hasDialogEdit = (valueType === 'object' || valueType === 'array'
			|| (valueType === 'string'
				&& dialogEditFormats.includes(schema.format))
		);

		if (!hasDialogEdit) {
			return;
		}
		const hide = (afterHide) => {
			this.setState({
				editorDialog: {
					show: false,
					json: editorDialog.json
				}
			}, afterHide);
		};

		let value;
		const json = editorDialog && editorDialog.json;
		if (json) {
			value = this.props.value;
		} else {
			try {
				value = JSON.parse(this.props.value);
			} catch (e) {
				// Invalid json encoding, display as is.
			}
		}

		return (
			<FieldEditorDialog
				name={name}
				schema={schema}
				value={value}
				show={editorDialog && editorDialog.show}
				json={json}
				onOkay={(v) => hide(() => this.onChangeValue(json ? v : JSON.stringify(v)))}
				onClose={hide}
				// currently we hide the schema for the types of editors that are for
				// certain lanaguages. Currently this is the same ones we render with
				// a button to open the editor.
				hideSchema={buttonFieldFormats.includes(valueFormat)}
			/>
		);
	}

	render() {
		const { required, enabled, invalid, intl, deletable } = this.props;
		let className = '';
		if (required) {
			className = 'required';
		} else if (enabled === false) {
			className = 'disabled';
		} else if (deletable) {
			className = 'deletable';
		}
		className = cn('nodeconfigfield', className);
		return (
			<FormGroup
				className={className}
				validationState={invalid ? 'error' : null}
			>
				{ this.renderBanner() }
				{ this.renderDescription() }
				{ this.renderDefaultValue() }
				{ this.renderInput() }
				{ invalid && <HelpBlock>{intl.formatMessage({ id: invalid })}</HelpBlock> }
				{ this.renderFieldEditModal() }
			</FormGroup>
		);
	}
}

NodeConfigField.propTypes = {
	id: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	required: PropTypes.bool,
	showSchema: PropTypes.bool,
	deletable: PropTypes.bool,
	readOnly: PropTypes.bool,
	schema: PropTypes.object.isRequired,
	enabled: PropTypes.bool,
	description: PropTypes.string,
	label: PropTypes.string,
	value: PropTypes.string,
	valueType: PropTypes.string.isRequired,
	valueFormat: PropTypes.string,
	inputTypes: PropTypes.arrayOf(PropTypes.string),
	onChange: PropTypes.func,
	onEnable: PropTypes.func,
	selectors: PropTypes.arrayOf(PropTypes.string),
	invalid: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.string
	]),
	/*
	 * The available credentials that can be set on a node
	 */
	credentials: PropTypes.object
};

NodeConfigField.defaultProps = {
	inputTypes: [
		'jsonpath',
		'string',
		'number',
		'boolean',
		'object',
		'array',
		'null',
		'credential'
	]
};

NodeConfigField.supportedEnumTypes = [ 'string', 'number' ];

export default injectIntl(NodeConfigField);
